/**
 * InlineDiffPreview — a standalone, presentational Apple-Writing-Tools-style
 * inline redline for a single text field (Jul 2026).
 *
 * Renders `original` with the AI's `changes` interleaved in place: inserted
 * text underlined emerald, deleted text struck rose, each with a tiny per-change
 * accept (Check) / reject (X) control. Pure + zero-dep (no `@ando/core`,
 * no store) so it drops into the `InlineAIRefiner` bubble OR any HITL card body.
 *
 * Decision model mirrors `editors/ReviewRail` + `diffStore.setDecision`, but
 * LIGHTWEIGHT + non-doc-keyed: the host owns a plain
 * `Record<changeId, 'accepted' | 'rejected'>` and a single `onDecision`
 * callback. A change with NO entry is PENDING — previewed as applied (the
 * default view shows the full rewrite; the user rejects only what they dislike).
 *
 * ── Diff contract ────────────────────────────────────────────────────────────
 *   - `changes` are ORDERED and NON-OVERLAPPING over `original`.
 *   - EQUAL runs (untouched text between changes) are OMITTED — recovered here
 *     by anchoring each change's `before` inside `original` from a monotonic
 *     cursor (safe because the stream is ordered + non-overlapping).
 *   - `insert`  → `before === ""`, `after` = inserted text.
 *   - `delete`  → `before` = removed text, `after === ""`.
 *   - `replace` → both non-empty.
 */

import { msg } from "@lingui/core/macro";
import { Check, X } from "lucide-react";
import * as React from "react";

import { cn } from "../../lib/cn";
import { useI18n } from "../../lib/i18n";

// ---------------------------------------------------------------------------
// Public model
// ---------------------------------------------------------------------------

export type DiffChangeType = "insert" | "delete" | "replace";

/** One atomic, non-overlapping change over `original`. */
export interface DiffChange {
  readonly id: string;
  readonly type: DiffChangeType;
  /** Original span this change covers ("" for a pure insert). */
  readonly before: string;
  /** Replacement span ("" for a pure delete). */
  readonly after: string;
}

/** Explicit per-change decision. Absence of an entry = pending (previewed applied). */
export type DiffDecision = "accepted" | "rejected";

export interface InlineDiffPreviewProps {
  readonly original: string;
  readonly changes: ReadonlyArray<DiffChange>;
  /** Per-change decisions keyed by change id (missing key = pending). */
  readonly decisions: Record<string, DiffDecision>;
  readonly onDecision: (id: string, decision: DiffDecision) => void;
  readonly className?: string;
}

// ---------------------------------------------------------------------------
// Pure diff walk (shared by the renderer + reconstructText)
// ---------------------------------------------------------------------------

type Segment =
  | { readonly kind: "equal"; readonly text: string }
  | { readonly kind: "change"; readonly change: DiffChange };

/**
 * Interleave the omitted equal runs back with the changes into an ordered
 * segment list. Anchors each change's `before` inside `original` from a
 * monotonic cursor; a pure insert (`before === ""`) lands at the current cursor
 * (the boundary of the previous anchored change / run). If a `before` anchor
 * can't be located the change is emitted in place WITHOUT advancing the cursor,
 * so the function is total (never throws / drops text).
 */
function buildSegments(
  original: string,
  changes: ReadonlyArray<DiffChange>,
): ReadonlyArray<Segment> {
  const segments: Segment[] = [];
  let cursor = 0;
  for (const change of changes) {
    if (change.before.length > 0) {
      const at = original.indexOf(change.before, cursor);
      if (at < 0) {
        // Anchor lost — emit in place, keep cursor so trailing text survives.
        segments.push({ kind: "change", change });
        continue;
      }
      if (at > cursor) segments.push({ kind: "equal", text: original.slice(cursor, at) });
      segments.push({ kind: "change", change });
      cursor = at + change.before.length;
    } else {
      segments.push({ kind: "change", change });
    }
  }
  if (cursor < original.length) segments.push({ kind: "equal", text: original.slice(cursor) });
  return segments;
}

/**
 * Reconstruct the final text from per-change decisions.
 *
 * Walks `original` (recovering the omitted equal runs by anchoring) and, per
 * change, substitutes: `rejected` → keep `before` (the original span);
 * everything else (`accepted` OR pending) → use `after` (the rewrite). Untouched
 * runs pass through verbatim. Equal runs are the parts of `original` BETWEEN the
 * ordered change anchors — the changes never cover them, so they always survive.
 */
export function reconstructText(
  original: string,
  changes: ReadonlyArray<DiffChange>,
  decisions: Record<string, DiffDecision>,
): string {
  if (changes.length === 0) return original;
  let out = "";
  for (const seg of buildSegments(original, changes)) {
    if (seg.kind === "equal") {
      out += seg.text;
    } else {
      out += decisions[seg.change.id] === "rejected" ? seg.change.before : seg.change.after;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const InlineDiffPreview: React.FC<InlineDiffPreviewProps> = ({
  original,
  changes,
  decisions,
  onDecision,
  className,
}) => {
  const { i18n } = useI18n();
  const segments = React.useMemo(() => buildSegments(original, changes), [original, changes]);
  return (
    <div
      className={cn(
        "max-h-[40vh] overflow-y-auto whitespace-pre-wrap break-words",
        // `leading-[1.9]`: i cambi hanno un fondo colorato: con l'interlinea
        // stretta i rettangoli di righe vicine si toccano.
        "text-[12.5px] text-ink-1 leading-[1.9]",
        className,
      )}
      aria-label={i18n._(msg`Anteprima delle modifiche`)}
    >
      {segments.map((seg, index) =>
        seg.kind === "equal" ? (
          <span key={keyForEqual(seg.text, index)}>{seg.text}</span>
        ) : (
          <ChangeSpan
            key={seg.change.id}
            change={seg.change}
            decision={decisions[seg.change.id]}
            onDecision={onDecision}
          />
        ),
      )}
    </div>
  );
};
InlineDiffPreview.displayName = "InlineDiffPreview";

/** Deterministic, index-free-ish key for an untouched run. */
function keyForEqual(text: string, index: number): string {
  return `eq-${index}-${text.length}`;
}

// ---------------------------------------------------------------------------
// One redlined change, inline, with tiny accept / reject controls
// ---------------------------------------------------------------------------

interface ChangeSpanProps {
  readonly change: DiffChange;
  readonly decision: DiffDecision | undefined;
  readonly onDecision: (id: string, decision: DiffDecision) => void;
}

/** Un cambio fatto solo di spazi/ritorni a capo: niente fondo colorato. */
function isBlank(text: string | undefined): boolean {
  return !text || text.trim() === "";
}

/**
 * Un singolo cambio in linea.
 *
 * Tre regole di layout, tutte imparate sbagliando:
 *  1. **I controlli stanno a fianco, in linea.** Prima erano in un popover
 *     assoluto: ancorato a uno span *inline*, quando il cambio andava a capo
 *     il browser lo attaccava al primo frammento e i ✓/✗ finivano in punti
 *     casuali, sopra il testo. Ora seguono il cambio come una parola qualsiasi.
 *  2. **I controlli sono sempre visibili**, in grigio tenue: si colorano
 *     (verde / rosso) al passaggio del mouse e restano accesi una volta
 *     decisi. Farli comparire solo all'hover li rendeva scopribili per caso,
 *     e mostrarli *aggiungendo* spazio faceva ballare la riga.
 *  3. **Vecchio e nuovo non si toccano.** Erano incollati — "relativa" +
 *     "riguardante" si leggeva "relativariguardante". Ognuno ha il suo fondo
 *     tenue, un filo di padding e un piccolo stacco.
 */
const ChangeSpan: React.FC<ChangeSpanProps> = ({ change, decision, onDecision }) => {
  const { i18n } = useI18n();
  const applied = decision !== "rejected"; // pending + accepted both preview as applied
  const chip = "rounded-[3px] px-[3px] py-[1px] decoration-1";
  const controlBase = cn(
    "grid h-4 w-4 place-items-center rounded-sm transition-colors duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
    "motion-reduce:transition-none",
  );
  return (
    <span className="group/change inline whitespace-pre-wrap">
      {change.before ? (
        <span
          className={cn(
            !isBlank(change.before) && chip,
            applied
              ? "bg-rose-500/[0.08] text-ink-3 line-through decoration-rose-400"
              : "bg-transparent text-ink-1",
          )}
        >
          {change.before}
        </span>
      ) : null}
      {change.before && change.after ? <span className="inline-block w-[3px]" /> : null}
      {change.after ? (
        <span
          className={cn(
            !isBlank(change.after) && chip,
            applied
              ? "bg-emerald-500/[0.12] text-emerald-700"
              : // Rifiutata: resta visibile ma spenta, così il controllo è ancora raggiungibile.
                "text-ink-4 line-through decoration-rose-400/50",
          )}
        >
          {change.after}
        </span>
      ) : null}
      <span className="ml-0.5 inline-flex w-[34px] items-center justify-center gap-px align-middle">
        <button
          type="button"
          onClick={() => onDecision(change.id, "accepted")}
          aria-pressed={decision === "accepted"}
          aria-label={i18n._(msg`Accetta modifica`)}
          className={cn(
            controlBase,
            decision === "accepted"
              ? "bg-emerald-500/15 text-emerald-600"
              : "text-ink-3 hover:bg-emerald-500/10 hover:text-emerald-600",
          )}
        >
          <Check className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => onDecision(change.id, "rejected")}
          aria-pressed={decision === "rejected"}
          aria-label={i18n._(msg`Rifiuta modifica`)}
          className={cn(
            controlBase,
            decision === "rejected"
              ? "bg-rose-500/15 text-rose-600"
              : "text-ink-3 hover:bg-rose-500/10 hover:text-rose-600",
          )}
        >
          <X className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden />
        </button>
      </span>
    </span>
  );
};
